import type { ReactNode } from 'react';
import type { AppRole } from '@exigo/shared';
import { useAuth } from './AuthProvider';
import { EmptyState } from '../components/ui/EmptyState';

interface RequireRoleProps {
  // Named requiredRole, not role: jsx-a11y's aria-role check flags any prop
  // literally called `role` as if it were the DOM ARIA attribute, and the name
  // invites that confusion anyway on a component with no DOM role of its own.
  requiredRole: AppRole;
  children: ReactNode;
}

/**
 * Gates a route on a role. This is a UX convenience only — the real
 * authorisation boundary is server-side (`requireAdmin` as an onRequest hook on
 * the `/api/v1/admin` prefix, security.md §4). Hiding a link here does not make
 * an endpoint safe; it only avoids showing an ordinary user a page that would
 * 403 anyway.
 */
export function RequireRole({ requiredRole, children }: RequireRoleProps): React.JSX.Element {
  const { user } = useAuth();

  if (!user?.roles.includes(requiredRole)) {
    return (
      <EmptyState
        title="Not available"
        body={
          requiredRole === 'Admin'
            ? 'This area is for administrators. Ask an administrator to change your role if you need access.'
            : 'This area is not available to your account.'
        }
      />
    );
  }

  return <>{children}</>;
}
